import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';

import { EllipsisVertical } from 'lucide-react';
import queryString from 'query-string';
import { Select, Row, Col, FormGroup, Label, Input, ButtonIcon, Switch, TabsList, Tabs, TabsTrigger, TabsContent, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'twyne-new.ui'

import InfoTip from '@Components/InfoTip';
import history from '@Utils/history';
import Rules from 'components/rules/Rules';

import { PATH_PING_POST_RULES } from '../../../../../settings/views/integration/components/utils';
import { usePageStore } from '../../store';

const propTypes = {
  show: PropTypes.bool,
  data: PropTypes.object,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
}

function PingPostTab({ data, onChangeData, integration, onChangeIntegration, disabled }) {
  const { integrationId, showWhenNotAvailable } = data || {}
  const selectedIntegration = integrationId && integration ? integration : null

  const handleIntegration = useCallback(option => {
    const { value } = option || {}
    onChangeData('integrationId', value || null)
  }, [onChangeData])

  const handleSelectIntegration = useCallback(option => {
    const { integration } = option || {}
    onChangeIntegration({ integration: integration || null })
  }, [onChangeIntegration])

  const handleToggle = useCallback(event => {
    const { checked } = event.target
    onChangeData('showWhenNotAvailable', checked)
  }, [onChangeData])

  return <>
    <Row>
      <Integration onChange={handleIntegration} value={integrationId} onSelect={handleSelectIntegration} integration={selectedIntegration} disabled={disabled} />
      <PingPostConfiguration onChange={handleToggle} value={showWhenNotAvailable} integration={integration || {}} disabled={disabled} />
      <IntegrationDetails integration={selectedIntegration} />
    </Row>
  </>
}

function Integration({ value, onChange, onSelect, integration, disabled }) {
  const { lists: { integrations: { useLoad, items: integrations } } } = usePageStore()
  const [options, setOptions] = useState([])

  const loadIntegrations = useLoad()

  useEffect(() => {
    if (integrations?.length) {
      const options = []
      integrations.forEach(integration => {
        const { id, description, integration_type, active } = integration
        if (['path_ping_post'].includes(integration_type) && active) {
          options.push({ value: id, label: `[#${id}] ${description}`, integration })
        }
      })
      setOptions(options)
    }
  }, [integrations])

  useEffect(() => {
    if (value && !integration) {
      onSelect(options.find(option => option.value === value) || null)
    }
  }, [value, options, onSelect, integration])

  const handleAdd = useCallback(() => {
    try {
      const state = JSON.stringify({ integration_type: 'path_ping_post' })
      const params = btoa(state)
      const search = queryString.stringify({ configure: params })
      window.open(history.createHref({ pathname: '/settings/integrations/add', search }))
    } catch (error) {
      console.error(error)
    }
  }, [])

  const handleEdit = useCallback(() => {
    try {
      window.open(history.createHref({ pathname: `/settings/integrations/${value}` }))
    } catch (error) {
      console.error(error)
    }
  }, [value])

  const handleChange = useCallback(option => {
    onSelect(option)
    onChange(option)
  }, [onChange, onSelect])

  return <Col>
    <FormGroup>
      <Label>Integration</Label>
      <div className='flex items-center gap-2'>
        <div className='grow'>
          <Select clearable name='integrationId' value={value} options={options} onChange={handleChange} disabled={disabled} />
        </div>
        <DropdownMenu optionsLabel='Integration'>
          <DropdownMenuTrigger>
            <ButtonIcon content='Integration options' disabled={disabled}>
              <EllipsisVertical size={16} />
            </ButtonIcon>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={handleAdd}>
              Add
            </DropdownMenuItem>
            <DropdownMenuItem disabled={!value} onClick={handleEdit}>
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={loadIntegrations}>
              Reload
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </FormGroup>
  </Col>
}

function PingPostConfiguration({ value, onChange, integration, disabled }) {
  if (integration?.configuration?.mode !== 'one_to_one_ping_post') {
    return null
  }

  return <Col size='12'>
    <FormGroup>
      <Label>
        Show when not available
        <InfoTip id='not-available' color='neutral' disabled>
          Show page even when there is no ping response or no buyer found (available for 1:1 Ping - Post).
        </InfoTip>
      </Label>
      <Switch onChange={onChange}
        value={value || false} disabled={disabled} />
    </FormGroup>
  </Col>
}

function IntegrationDetails({ integration }) {
  const { configuration, rules } = integration || {}

  if (!configuration) {
    return null
  }

  return <Col>
    <Tabs defaultValue='details' size='sm'>
      <TabsList>
        <TabsTrigger className='pt-0' value='details'>Details</TabsTrigger>
        {rules && <TabsTrigger className='pt-0' value='rules'>Rules</TabsTrigger>}
        {configuration?.customQuestions?.length ? <TabsTrigger className='pt-0' value='custom-questions'>Custom Questions</TabsTrigger> : null}
        {configuration?.customFields?.length ? <TabsTrigger className='pt-0' value='custom-fields'>Custom Fields</TabsTrigger> : null}
        {configuration?.click?.length ? <TabsTrigger className='pt-0' value='click'>Click</TabsTrigger> : null}
      </TabsList>
      <TabsContent value='details'>
        <Details configuration={configuration} />
      </TabsContent>
      <TabsContent value='rules'>
        <RulesDetails rules={rules} />
      </TabsContent>
      <TabsContent value='custom-questions'>
        <Parameters parameters={configuration.customQuestions} />
      </TabsContent>
      <TabsContent value='custom-fields'>
        <Parameters parameters={configuration.customFields} />
      </TabsContent>
      <TabsContent value='click'>
        <Parameters parameters={configuration.click} />
      </TabsContent>
    </Tabs>
  </Col>
}

function Details({ configuration }) {
  const MODE = {
    one_to_one_ping_post: '1:1 Ping - Post',
    ping_post: 'Ping - Post',
    only_post: 'Direct Post',
  }

  const DETAILS = [
    { label: "Mode", field: "mode", size: 12, value: MODE[configuration.mode] },
    { label: "URL", field: "url", size: 12 },
    { label: "Publisher", field: "publisher", size: 4 },
    { label: "Source", field: "source", size: 4 },
    { label: "Campaign", field: "campaign", size: 4 },
  ]

  return <Row>
    {DETAILS.map(({ label, field, size, value }) => <Col key={field} size={size}>
      <FormGroup>
        <Label>{label}</Label>
        <Input value={value || configuration[field]} disabled />
      </FormGroup>
    </Col>)}
  </Row>
}

function Parameters({ label, parameters }) {
  if (!parameters) {
    return null
  }

  return <Row className="pb-3">
    {parameters.map(({ key, value, static_value }) => <>
      <Col size="6" className="pb-3">
        <Input value={key} disabled />
      </Col>
      <Col size="6" className="pb-3">
        <Input value={value || static_value} disabled />
      </Col>
    </>)}
  </Row>
}

function RulesDetails({ rules }) {
  if (!rules) {
    return null;
  }

  return <Row>
    <Col size="12">
      <Rules disabled={true} origin='integration' rules={rules}
        extraFields={PATH_PING_POST_RULES} integrationType='path_ping_post' />
    </Col>
  </Row>
}

PingPostTab.propTypes = propTypes;

export default PingPostTab;
